import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { initializeAnalytics, hasAnalyticsConsent } from '../utils/analytics';

const CONSENT_KEY = 'cookie_consent';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(CONSENT_KEY);
    if (consent === null) {
      // No choice made yet - show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else if (consent === 'accepted') {
      // User previously accepted - initialize analytics
      initializeAnalytics();
    }
    // If consent === 'declined', do nothing (don't load analytics)
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    initializeAnalytics();
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleCustomize = () => {
    setShowSettings(true);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop for settings modal */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998]"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl z-[9999] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Cookie Settings
            </h3>
            <button
              onClick={() => setShowSettings(false)}
              className="p-1 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 mb-6">
            {/* Essential Cookies */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">Essential Cookies</span>
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Always On</span>
              </div>
              <p className="text-sm text-slate-400">
                Required for the website to function. These include authentication, security, and basic functionality.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-white">Analytics Cookies</span>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Optional</span>
              </div>
              <p className="text-sm text-slate-400">
                Help us understand how visitors interact with our website using Google Analytics. Data is anonymized and used to improve user experience.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors font-medium"
            >
              Essential Only
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors font-medium"
            >
              Accept All
            </button>
          </div>
        </div>
      )}

      {/* Main Banner */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6">
          <div className="max-w-4xl mx-auto bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Icon and Text */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-amber-400" />
                  <h3 className="font-semibold text-white">We value your privacy</h3>
                </div>
                <p className="text-sm text-slate-400">
                  We use cookies to enhance your browsing experience and analyze site traffic.
                  By clicking "Accept All", you consent to our use of cookies.
                  Read our{' '}
                  <Link to="/privacy" className="text-indigo-400 hover:text-indigo-300 underline">
                    Privacy Policy
                  </Link>{' '}
                  for more information.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleCustomize}
                  className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors font-medium"
                >
                  Customize
                </button>
                <button
                  onClick={handleDecline}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors text-sm font-medium"
                >
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors text-sm font-medium"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
